import { AfterViewInit, Directive, ElementRef, HostListener, Input, OnDestroy } from '@angular/core';
import { FloatingDialogLayoutService } from '../../core/services/floating-dialog-layout.service';

/**
 * Drag the host Material dialog by moving its enclosing `.cdk-overlay-pane`.
 * Attach to a title bar or header row; interactive controls are excluded from starting a drag.
 */
@Directive({
  selector: '[appDraggableOverlayPane]'
})
export class DraggableOverlayPaneDirective implements AfterViewInit, OnDestroy {
  /** When set, position/size is saved to localStorage after each drag */
  @Input() dragLayoutKey?: string;

  private pane?: HTMLElement;
  private moveListener?: (e: MouseEvent) => void;
  private upListener?: () => void;

  constructor(
    private el: ElementRef<HTMLElement>,
    private layout: FloatingDialogLayoutService
  ) {}

  ngAfterViewInit(): void {
    this.pane = this.el.nativeElement.closest('.cdk-overlay-pane') ?? undefined;
  }

  ngOnDestroy(): void {
    this.endDrag();
  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent): void {
    if (event.button !== 0 || !this.pane) {
      return;
    }
    const target = event.target as HTMLElement;
    if (target.closest('button, a, input, textarea, select, [mat-dialog-close], [mat-icon-button]')) {
      return;
    }

    event.preventDefault();

    const rect = this.pane.getBoundingClientRect();
    const startLeft = rect.left;
    const startTop = rect.top;
    const startX = event.clientX;
    const startY = event.clientY;

    this.pane.style.position = 'fixed';
    this.pane.style.left = `${startLeft}px`;
    this.pane.style.top = `${startTop}px`;
    this.pane.style.margin = '0';
    this.pane.style.transform = '';

    this.moveListener = (ev: MouseEvent) => {
      if (!this.pane) {
        return;
      }
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const w = this.pane.offsetWidth;
      const h = this.pane.offsetHeight;
      let left = startLeft + dx;
      let top = startTop + dy;
      const maxL = Math.max(0, window.innerWidth - w);
      const maxT = Math.max(0, window.innerHeight - h);
      left = Math.max(0, Math.min(left, maxL));
      top = Math.max(0, Math.min(top, maxT));
      this.pane.style.left = `${left}px`;
      this.pane.style.top = `${top}px`;
    };

    this.upListener = () => {
      if (this.moveListener) {
        document.removeEventListener('mousemove', this.moveListener);
        this.moveListener = undefined;
      }
      if (this.pane && this.dragLayoutKey) {
        this.layout.saveFromPane(this.dragLayoutKey, this.pane);
      }
      this.upListener = undefined;
    };

    document.addEventListener('mousemove', this.moveListener);
    document.addEventListener('mouseup', this.upListener, { once: true });
  }

  private endDrag(): void {
    if (this.moveListener) {
      document.removeEventListener('mousemove', this.moveListener);
      this.moveListener = undefined;
    }
    if (this.upListener) {
      document.removeEventListener('mouseup', this.upListener);
      this.upListener = undefined;
    }
  }
}
